/**
 * Life4Earth V2 — Light Theme
 *
 * Same token keys as dark.ts with light-mode values.
 * Accent colors are slightly darker for contrast on white.
 */
import { SLATE, BLUE, RED, GREEN, AMBER, PURPLE } from '../tokens/colors';
import type { ThemeTokens } from '../types';

export const LIGHT_THEME: ThemeTokens = {
  /* ---- Backgrounds ---- */
  '--bg-primary':   '#ffffff',
  '--bg-secondary': SLATE[50],                 // #f8fafc (legacy alias for --bg-surface)
  '--bg-surface':   SLATE[50],                 // #f8fafc — cards, widgets, panels
  '--bg-tertiary':  SLATE[100],                // #f1f5f9
  '--bg-elevated':  'rgba(255, 255, 255, 0.95)',
  '--bg-secondary-header': SLATE[200],         // #e2e8f0 — darkened --bg-secondary for section headers
  '--bg-primary-translucent': 'rgba(255, 255, 255, 0.92)', // --bg-primary at 92% — lets bg texture through

  /* ---- Text ---- */
  '--text-primary':   SLATE[950],  // #020617
  '--text-secondary': SLATE[800],  // #1e293b
  '--text-tertiary':  SLATE[600],  // #475569
  '--text-muted':     SLATE[500],  // #64748b

  /* ---- Accent / Interactive ---- */
  '--accent-primary':       BLUE[600],   // #2563eb  (darker for white bg)
  '--accent-primary-hover': BLUE[700],   // #1d4ed8
  '--accent-secondary':     PURPLE[600], // #9333ea

  /* ---- Secondary action (amber/gold) ---- */
  '--action-secondary':       AMBER[700],  // #b45309 (darker for light bg)
  '--action-secondary-hover': AMBER[800],  // #92400e
  '--action-secondary-bg':    'rgba(180, 83, 9, 0.10)',

  /* ---- Semantic status ---- */
  '--color-success': GREEN[600],  // #16a34a
  '--color-warning': AMBER[600],  // #d97706
  '--color-error':   RED[600],    // #dc2626

  /* ---- Pillar brand colors ---- */
  '--pillar-problems':    RED[600],
  '--pillar-solutions':   GREEN[600],
  '--pillar-enablers':    BLUE[600],
  '--pillar-communities': SLATE[400],
  '--pillar-projects':    SLATE[600],   // #475569 — dark grey

  /* ---- Content bookshelf spine colors (§5.1 — Proposed palette) ---- */
  '--content-spine-tracker':   '#0891b2',   // cyan-600
  // Blog (internal key: journal_entry). Teal, not amber — amber is --color-warning.
  '--content-spine-journal':   '#0d9488',   // teal-600
  '--content-spine-howto':     '#c2410c',   // orange-700
  '--content-spine-mini':      '#65a30d',   // lime-600
  '--content-spine-chronicle': '#4f46e5',   // indigo-600
  '--content-spine-lesson':    '#e11d48',   // rose-600
  '--content-spine-practice':  '#7c3aed',   // violet-600
  '--content-spine-news':      '#c026d3',   // fuchsia-600
  '--content-spine-scholarly': '#57534e',   // stone-600

  /* ---- Interactive surfaces ---- */
  '--bg-hover':          'rgba(0, 0, 0, 0.03)',
  '--bg-hover-strong':   'rgba(0, 0, 0, 0.06)',
  '--bg-active':         'rgba(0, 0, 0, 0.08)',
  '--accent-bg':         'rgba(37, 99, 235, 0.10)',
  '--accent-bg-40':      'rgba(37, 99, 235, 0.20)',
  '--accent-border':     'rgba(37, 99, 235, 0.30)',
  '--accent-text':       '#1d4ed8',
  '--accent-secondary-bg':      'rgba(147, 51, 234, 0.10)',
  '--accent-secondary-bg-soft': 'rgba(147, 51, 234, 0.05)',
  '--accent-secondary-border':  'rgba(147, 51, 234, 0.20)',
  '--info-bg':           'rgba(37, 99, 235, 0.10)',
  '--info-border':       'rgba(37, 99, 235, 0.20)',
  '--success-bg':        'rgba(22, 163, 74, 0.10)',
  '--success-bg-strong': 'rgba(22, 163, 74, 0.16)',
  '--success-border':    'rgba(22, 163, 74, 0.20)',
  '--text-on-accent':    '#ffffff',

  /* ---- Borders ---- */
  '--border-subtle':  'rgba(2, 6, 23, 0.08)',
  '--border-default': 'rgba(2, 6, 23, 0.15)',
  '--border-strong':  'rgba(2, 6, 23, 0.3)',

  /* ---- Cards ---- */
  '--card-bg':     '#ffffff',
  '--card-border': 'rgba(2, 6, 23, 0.12)',

  /* ---- Chart canvas ----
   * DEV-845: Canonical "data well" surface inside a card. Slightly off-white
   * in light theme so the chart canvas reads as a recessed zone vs the white
   * card chrome around it. Custom themes can override via the theme designer. */
  '--chart-canvas-bg': '#f5f7fa',

  /* ---- Card scrims & overlays ---- */
  '--card-scrim':       'rgba(0, 0, 0, 0.55)',
  '--card-overlay':     'rgba(0, 0, 0, 0.65)',
  '--card-scrim-blur':  '8px',

  /* ---- Status text colors ---- */
  '--success-text':   '#16a34a',
  '--info-text':      '#2563eb',
  '--warning-text':   '#d97706',
  '--error-text':     '#dc2626',

  /* ---- Error status ---- */
  '--error-bg':     'rgba(220, 38, 38, 0.08)',
  '--error-border': 'rgba(220, 38, 38, 0.20)',
  '--warning-bg':   'rgba(217, 119, 6, 0.08)',
  '--warning-border': 'rgba(217, 119, 6, 0.20)',

  /* ---- Overlay / Scrim ---- */
  '--bg-scrim': 'rgba(0, 0, 0, 0.4)',

  /* ---- Shadows ---- */
  '--shadow-sm':   '0 1px 2px 0 rgba(0, 0, 0, 0.04)',
  '--shadow-base': '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
  '--shadow-lg':   '0 20px 25px -5px rgba(0, 0, 0, 0.1)',

  /* ---- Transitions ---- */
  '--duration-fast': '120ms',
  '--duration-base': '150ms',
  '--duration-slow': '200ms',
  '--easing-out':    'cubic-bezier(0, 0, 0.2, 1)',

  /* ---- Left Navigation ---- */
  '--leftnav-shadow':         '2px 0 6px rgba(0, 0, 0, 0.06)',
  '--leftnav-bg':             '#ffffff',                    // matches --bg-primary (light)
  '--leftnav-border':         'rgba(2, 6, 23, 0.08)',      // matches --border-subtle
  '--leftnav-item-text':      SLATE[800],                   // #1e293b — matches --text-secondary
  '--leftnav-item-hover':     'rgba(0, 0, 0, 0.06)',       // matches --bg-hover-strong
  '--leftnav-item-active-bg': 'rgba(37, 99, 235, 0.10)',   // matches --accent-bg
  '--leftnav-item-active-border': 'rgba(37, 99, 235, 0.30)', // matches --accent-border
  '--leftnav-item-active-text': SLATE[950],                 // #020617 — matches --text-primary
  '--leftnav-group-label':    SLATE[500],                   // #64748b — matches --text-muted

  /* ---- Rail (POLICY_NAVIGATION_RAILS.md) ----
   * Mirrors --leftnav-* in light theme. Rail bg is a hair off-white
   * (SLATE[50]) so it separates subtly from the pure-white LeftNav while
   * both stay opaque over content. */
  '--rail-width-switcher':    '220px',
  '--rail-width-list':        '340px',
  '--rail-width-collapsed':   '56px',
  '--rail-breakpoint':        '960px',
  '--rail-shadow':            '2px 0 6px rgba(0, 0, 0, 0.06)',
  '--rail-bg':                SLATE[50],                    // #f8fafc — one step off the white --leftnav-bg
  '--rail-border':            'rgba(2, 6, 23, 0.08)',      // matches --border-subtle
  '--rail-item-text':         SLATE[800],                   // #1e293b — matches --text-secondary
  '--rail-item-muted':        SLATE[500],                   // #64748b — matches --text-muted
  '--rail-item-hover':        'rgba(0, 0, 0, 0.05)',
  '--rail-item-active-bg':    'rgba(37, 99, 235, 0.10)',   // matches --accent-bg
  '--rail-item-active-border':'rgba(37, 99, 235, 0.30)',   // matches --accent-border
  '--rail-item-active-text':  SLATE[950],                   // #020617 — matches --text-primary
  '--rail-header-label':      SLATE[500],                   // #64748b — matches --text-muted

  /* ---- Topbar specific ---- */
  '--topbar-shadow':  '0 2px 6px rgba(0, 0, 0, 0.06)',
  '--topbar-bg':     'rgba(255, 255, 255, 0.85)',
  '--topbar-border': 'rgba(2, 6, 23, 0.1)',

  /* ---- Header glass effect ---- */
  '--header-glass-bg':  'rgba(248, 250, 252, 0.82)',
  '--header-glass-tab': 'rgba(248, 250, 252, 0.82)',

  /* ---- Scrollbar ---- */
  '--scrollbar-track': SLATE[100],
  '--scrollbar-thumb': SLATE[300],

  /* ---- Impact Signature: Dimension colors (slightly deeper for light bg) ---- */
  '--dim-mind':  '#5a87b5',
  '--dim-voice': '#3d8f99',
  '--dim-heart': '#b5685d',
  '--dim-body':  '#4d9160',
  '--dim-wings': '#b08a3a',
  '--dim-roots': '#6E5B3E',

  /* ---- Impact Signature: Medal tier colors (slightly deeper for light bg) ---- */
  '--medal-bronze':   '#B56E28',
  '--medal-silver':   '#8E97A3',
  '--medal-gold':     '#BF943A',
  '--medal-platinum': '#A8A6A2',

  /* ---- Globe / Discover Shader ---- */
  '--globe-bg-dark':   '#0a0a14',     // softer black-violet for light theme
  '--globe-bg-mid':    '#1a1a3a',     // muted indigo center
  '--globe-star-color':'#e8e8f0',     // slightly cooler white stars
  '--globe-glow':      '#38bdf8',     // sky-blue rim glow
};
